// config/menuAdminConfig.js
//
// El menú del panel, declarado por PERMISO y no por rol.
//
// Antes cada entrada traía su propia lista (`user?.isAdmin || user?.isCoordinador ||
// …`). Eso tenía dos costos: crear un rol nuevo obligaba a editar este archivo, y el
// menú podía desalinearse de las guardas de `App.js` sin que nada lo notara —pasaba:
// la entrada "Operaciones" se le ofrecía al jefe de recinto y la ruta lo rebotaba a
// /unauthorized—. Ahora ambos preguntan lo mismo: `can(user, modulo, accion)`.
import {
  AppstoreOutlined,
  CloudUploadOutlined,
  TeamOutlined,
  PercentageOutlined,
  BarChartOutlined,
  FileTextOutlined,
  AuditOutlined,
  DollarOutlined,
  LineChartOutlined,
  FundOutlined,
  DashboardOutlined,
  UserOutlined,
  IdcardOutlined,
  SettingOutlined,
  UnorderedListOutlined,
  EnvironmentOutlined,
  RiseOutlined,
  TrophyOutlined,
  TagOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  MonitorOutlined,
  SafetyCertificateOutlined,
  MailOutlined,
  CalendarOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { FaWarehouse } from 'react-icons/fa';
import { can, puedeAprobar } from './rbac';

// Azúcar para que cada entrada se lea como lo que es: un permiso.
const ver = modulo => user => can(user, modulo, 'ver');
const editar = modulo => user => can(user, modulo, 'editar');

// Definir todos los items disponibles con sus permisos
export const MENU_ITEMS = {
  dashboard: {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Inicio',
    canAccess: ver('dashboard'),
  },
  usuarios: {
    key: 'usuarios',
    icon: <TeamOutlined />,
    label: 'Usuarios',
    canAccess: ver('usuarios'),
  },
  roles: {
    key: 'roles',
    icon: <SafetyCertificateOutlined />,
    label: 'Roles y permisos',
    canAccess: ver('roles'),
  },
  recintos: {
    key: 'recintos',
    icon: <FaWarehouse />,
    label: 'Recintos',
    canAccess: ver('recintos'),
  },
  talleres: {
    key: 'talleres',
    icon: <AppstoreOutlined />,
    label: 'Talleres',
    canAccess: ver('talleres'),
    children: [
      {
        key: 'talleres/lista',
        label: 'Lista de Talleres',
        canAccess: ver('talleres'),
      },
      {
        key: 'talleres/cambios-revisiones',
        label: 'Cambios y Revisiones',
        icon: <AuditOutlined />,
        // La cola de revisión la ve quien puede resolverla. Quien necesita que le
        // aprueben no se aprueba a sí mismo, así que no la ve: es la misma condición
        // que protege el endpoint de aprobar.
        canAccess: user => puedeAprobar(user, 'talleres'),
      },
    ],
  },
  matriculas: {
    key: 'matriculas',
    icon: <IdcardOutlined />,
    label: 'Matrículas',
    canAccess: ver('matriculas'),
    children: [
      {
        key: 'matriculas/configuracion',
        label: 'Configuración de Recintos',
        icon: <SettingOutlined />,
        canAccess: editar('matriculas'),
      },
      {
        key: 'matriculas/usuarios',
        label: 'Usuarios Matriculados',
        icon: <TeamOutlined />,
        canAccess: ver('matriculas'),
      },
      {
        key: 'matriculas/estadisticas',
        label: 'Estadísticas',
        icon: <BarChartOutlined />,
        canAccess: editar('matriculas'),
      },
      {
        key: 'matriculas/manual',
        label: 'Registro Manual',
        icon: <FileTextOutlined />,
        canAccess: editar('matriculas'),
      },
      {
        key: 'matriculas/carga-masiva',
        label: 'Carga Masiva',
        icon: <CloudUploadOutlined />,
        canAccess: editar('matriculas'),
      },
      {
        key: 'matriculas/funcionarios',
        label: 'Funcionarios',
        icon: <IdcardOutlined />,
        canAccess: ver('matriculas'),
      },
    ],
  },
  ventas: {
    key: 'ventas',
    icon: <DollarOutlined />,
    label: 'Ventas',
    canAccess: ver('ventas'),
    children: [
      {
        key: 'ventas/nueva',
        label: 'Iniciar Venta',
        canAccess: editar('ventas'),
      },
    ],
  },
  // Servicios con hora (quiropraxia, masajes…) que el público compra desde el sitio
  // informativo. Todo se ve con `agenda:ver`; editar se decide dentro de cada pantalla.
  agenda: {
    key: 'agenda',
    icon: <CalendarOutlined />,
    label: 'Agendamiento',
    canAccess: ver('agenda'),
    children: [
      { key: 'agenda/reservas', label: 'Reservas', icon: <ScheduleOutlined />, canAccess: ver('agenda') },
      { key: 'agenda/servicios', label: 'Servicios', icon: <AppstoreOutlined />, canAccess: ver('agenda') },
      { key: 'agenda/profesionales', label: 'Profesionales y boxes', icon: <TeamOutlined />, canAccess: ver('agenda') },
      { key: 'agenda/ventas', label: 'Ventas', icon: <DollarOutlined />, canAccess: ver('agenda') },
    ],
  },
  waitingRoom: {
    key: 'waiting-room',
    icon: <ClockCircleOutlined />,
    label: 'Sala de Espera',
    canAccess: ver('sala-espera'),
  },
  operaciones: {
    key: 'operaciones',
    icon: <MonitorOutlined />,
    label: 'Operaciones',
    // La pantalla junta el torniquete con Casos y el cuadre de caja, que son dos
    // permisos distintos a propósito: quien cuida la puerta no tiene por qué manejar
    // reembolsos ni plata. Entra quien tenga cualquiera de los dos, y adentro solo ve
    // sus pestañas (ver Operaciones/index.jsx y `soloVeTorniquete`).
    canAccess: user => can(user, 'torniquete', 'ver') || can(user, 'casos', 'ver'),
  },
  comunicaciones: {
    key: 'comunicaciones',
    icon: <MailOutlined />,
    label: 'Comunicaciones',
    canAccess: ver('comunicaciones'),
  },
  descuentos: {
    key: 'descuentos',
    icon: <PercentageOutlined />,
    label: 'Descuentos',
    canAccess: ver('descuentos'),
    children: [
      {
        key: 'descuentos-automaticos',
        label: 'Automáticos',
        icon: <ThunderboltOutlined />,
        canAccess: ver('descuentos'),
      },
      {
        key: 'codigos-manuales',
        label: 'Códigos Manuales',
        icon: <TagOutlined />,
        canAccess: ver('descuentos'),
      },
    ],
  },
  reporteria: {
    key: 'reporteria',
    icon: <LineChartOutlined />,
    label: 'Reportería',
    canAccess: ver('reporteria'),
    children: [
      {
        key: 'reporteria/ventas-unificadas',
        label: 'Ventas Unificadas',
        icon: <UnorderedListOutlined />,
        canAccess: ver('reporteria'),
      },
      {
        key: 'reporteria/financiero',
        label: 'Reportes Financieros',
        icon: <FundOutlined />,
        canAccess: ver('reporteria'),
        children: [
          {
            key: 'reporteria/financiero/ingresos',
            label: 'Ingresos y Ventas',
            icon: <BarChartOutlined />,
            canAccess: ver('reporteria'),
          },
          {
            key: 'reporteria/financiero/descuentos',
            label: 'Descuentos y Precios',
            icon: <PercentageOutlined />,
            canAccess: ver('reporteria'),
          },
        ],
      },
      {
        key: 'reporteria/usuarios',
        label: 'Reportes de Usuarios',
        icon: <UserOutlined />,
        canAccess: ver('reporteria'),
        children: [
          {
            key: 'reporteria/usuarios/demografia',
            label: 'Datos Demográficos',
            icon: <EnvironmentOutlined />,
            canAccess: ver('reporteria'),
          },
          {
            key: 'reporteria/usuarios/comportamiento',
            label: 'Comportamiento y Retención',
            icon: <RiseOutlined />,
            canAccess: ver('reporteria'),
          },
        ],
      },
      {
        key: 'reporteria/ventas',
        label: 'Reportes de Ventas',
        icon: <TrophyOutlined />,
        canAccess: ver('reporteria'),
        children: [
          {
            key: 'reporteria/ventas/talleres',
            label: 'Ventas por Taller',
            icon: <TrophyOutlined />,
            canAccess: ver('reporteria'),
          },
        ],
      },
    ],
  },
};

// Función recursiva para filtrar children anidados
const filterMenuChildren = (children, user) => {
  if (!children) return null;

  return children
    .filter((child) => child.canAccess(user))
    .map((child) => {
      if (child.children) {
        const filteredGrandChildren = filterMenuChildren(child.children, user);
        if (filteredGrandChildren && filteredGrandChildren.length > 0) {
          return {
            ...child,
            children: filteredGrandChildren,
          };
        }
        return null;
      }
      return child;
    })
    .filter(Boolean);
};

/**
 * ¿Este usuario entra a Operaciones SOLO por el torniquete?
 *
 * Casos maneja reembolsos y notas de crédito, y el cuadre es plata. Quien tiene el
 * torniquete para abrirle a alguien en la puerta no necesita nada de eso.
 *
 * Esto ordena la interfaz; NO es una barrera de seguridad: eso son las guardas de los
 * endpoints.
 */
export const soloVeTorniquete = (user) =>
  can(user, 'torniquete', 'ver') && !can(user, 'casos', 'ver');

export const getMenuItemsByUser = (user) => {
  if (!user) {
    return [];
  }

  const filteredItems = Object.values(MENU_ITEMS)
    .filter((item) => item.canAccess(user))
    .map((item) => {
      // Si el item tiene children, filtrarlos recursivamente
      if (item.children) {
        const filteredChildren = filterMenuChildren(item.children, user);

        // Solo retornar el item si tiene children visibles
        if (filteredChildren && filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren,
          };
        }
        return null;
      }
      return item;
    })
    .filter(Boolean); // Remover items null

  return filteredItems;
};

// Función para verificar si un usuario puede acceder a una ruta específica
/**
 * Primera hoja navegable de un item del menú (baja por los submenús anidados).
 */
const primeraHoja = (item) => {
  if (!item) return null;
  if (item.children?.length) return primeraHoja(item.children[0]);
  return item.key ?? null;
};

/**
 * Primera ruta del panel que este usuario sí puede abrir.
 *
 * Se deriva del mismo menú ya filtrado que se dibuja, así que la puerta de entrada
 * y la navegación no pueden discrepar: si aparece como primer item del menú, es
 * una pantalla a la que tiene permiso.
 *
 * Antes `/admin` redirigía siempre a `/admin/dashboard`, de modo que un rol sin
 * `ver('dashboard')` aterrizaba en una pantalla que su propio menú le ocultaba.
 *
 * @returns {string|null} clave relativa ('ventas/nueva') o null si no ve nada.
 */
export const primeraRutaDelPanel = (user) => primeraHoja(getMenuItemsByUser(user)[0]);

export const canAccessRoute = (route, user) => {
  // Buscar en todos los niveles del menú
  const findRouteAccess = (items, routePath) => {
    for (const item of items) {
      if (item.key === routePath && item.canAccess(user)) {
        return true;
      }
      if (item.children) {
        if (findRouteAccess(item.children, routePath)) {
          return true;
        }
      }
    }
    return false;
  };

  return findRouteAccess(Object.values(MENU_ITEMS), route);
};

// Función auxiliar para obtener breadcrumbs de una ruta
export const getBreadcrumbsForRoute = (route) => {
  const parts = route.split('/');
  const breadcrumbs = [];

  let currentPath = '';
  for (const part of parts) {
    currentPath += (currentPath ? '/' : '') + part;

    // Buscar el label correspondiente en MENU_ITEMS
    const findLabel = (items, path) => {
      for (const item of items) {
        if (item.key === path) {
          return item.label;
        }
        if (item.children) {
          const childLabel = findLabel(item.children, path);
          if (childLabel) return childLabel;
        }
      }
      return null;
    };

    const label = findLabel(Object.values(MENU_ITEMS), currentPath);
    if (label) {
      breadcrumbs.push({
        path: currentPath,
        label: label,
      });
    }
  }

  return breadcrumbs;
};
